import { useMemo } from 'react';

import { urls } from '@repo/app-constants';
import { ButtonColors } from 'components';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { useMobileDetection } from 'hooks/useMobileDetection';

import { CARD_TITLE_COLORS } from '../constants/cardTitleColors';
import styles from '../RecommendedForYou.module.css';
import { getTinyPlusUpsellCard } from '../utils/getTinyPlusUpsellCard';

interface UseRecommendedCardsParams {
  latestKitId: string | undefined;
  checkoutUrl: string;
  isCheckoutExternal?: boolean;
}

export const useRecommendedCards = ({
  latestKitId,
  checkoutUrl,
  isCheckoutExternal = true,
}: UseRecommendedCardsParams) => {
  const { isMobile } = useMobileDetection();
  const { trackEvent } = useAnalytics();

  return useMemo(
    () => ({
      refer: {
        title: 'Give $40, Get $40',
        label: 'Refer a friend',
        imgStyles: styles.refer,
        cardColor: styles.colorRefer,
        titleColor: CARD_TITLE_COLORS.orange,
        buttonColor: ButtonColors.DARK_ORANGE,
        clickable: {
          href: '/refer-a-friend',
          text: 'Share',
          isExternal: false,
        },
        analyticsEventName: 'Clicked Button' as const,
      },
      resources: {
        title: (
          <>
            Resources to
            <br />
            help you
          </>
        ),
        label: (
          <>
            Find food guides,
            <br />
            articles, webinars
            <br />
            and more!
          </>
        ),
        imgStyles: styles.resources,
        cardColor: styles.colorResources,
        titleColor: CARD_TITLE_COLORS.green,
        buttonColor: ButtonColors.GREEN,
        clickable: {
          href: '/guides',
          text: 'Explore resources',
          isExternal: false,
        },
        analyticsEventName: 'Clicked Button' as const,
      },
      facebook: {
        title: 'Join our facebook community',
        label: '',
        imgStyles: styles.facebook,
        cardColor: styles.colorFacebook,
        titleColor: CARD_TITLE_COLORS.blue,
        buttonColor: ButtonColors.DARK_BLUE,
        clickable: {
          href: urls.FACEBOOK_GROUP,
          text: 'Visit group',
          isExternal: true,
        },
        analyticsEventName: 'Clicked Button' as const,
      },
      actions: {
        title: 'Your Action Plan is Ready',
        label: '',
        imgStyles: styles.actions,
        cardColor: styles.colorActions,
        titleColor: CARD_TITLE_COLORS.green,
        buttonColor: ButtonColors.GREEN,
        clickable: {
          href: `/action-plan/${latestKitId ?? ''}`,
          text: 'View Plan',
          isExternal: false,
        },
        analyticsEventName: 'Clicked Button' as const,
      },
      upsellTinyPlus: {
        ...getTinyPlusUpsellCard(
          'Have questions? Get answers.',
          <>
            Upgrade to Tiny+ to
            <br />
            unlock a free 1-1
            <br />
            coaching call.
          </>,
        ),
        leftColumnStyles: styles.upsellLeftColumn,
        clickable: {
          href: urls.TINY_PLUS_UPGRADE_OFFER,
          text: 'Upgrade to Tiny+',
          isExternal: true,
        },
        analyticsEventName: 'Click Home Tiny Plus Card' as const,
        onClick: () =>
          trackEvent('Membership Learn More Clicked', {
            source: 'home_recommended_card',
          }),
      },
      consult: {
        ...getTinyPlusUpsellCard(
          'Have questions? Get answers.',
          <>
            Tiny+ members get a free 1-1 and discounts on additional coaching
            calls.
          </>,
        ),
        leftColumnStyles: styles.upsellLeftColumn,
        clickable: {
          href: `/results/${latestKitId ?? ''}/consults/select-method`,
          text: 'Schedule',
          isExternal: false,
        },
        analyticsEventName: 'Click Home Tiny Plus Card' as const,
        onClick: () =>
          trackEvent('Schedule Call Clicked', {
            source: 'homepage_recommended',
          }),
      },
      consultAlt: {
        title: 'Have questions? Get answers.',
        label: 'Schedule a coaching call with our experts',
        imgStyles: styles.upsellConsult,
        cardColor: styles.colorUpsellConsult,
        titleColor: CARD_TITLE_COLORS.yellow,
        buttonColor: 'yellow-400' as const,
        leftColumnStyles: styles.upsellLeftColumn,
        clickable: {
          href: `/results/${latestKitId ?? ''}/consults/select-method`,
          text: 'Schedule',
          isExternal: false,
        },
        analyticsEventName: 'Click Home Tiny Plus Card' as const,
        onClick: () =>
          trackEvent('Schedule Call Clicked', {
            source: 'homepage_recommended',
          }),
      },
      order: {
        title: isMobile ? (
          <>
            Track your
            <br />
            progress
          </>
        ) : (
          'Track your progress'
        ),
        label: `Retest to see how things are doing.`,
        imgStyles: styles.order,
        cardColor: styles.colorOrder,
        titleColor: CARD_TITLE_COLORS.purple,
        buttonColor: ButtonColors.PURPLE,
        clickable: {
          href: checkoutUrl,
          text: `Buy a kit`,
          isExternal: isCheckoutExternal,
        },
        noOverlay: true,
        analyticsEventName: 'Clicked Button' as const,
      },
    }),
    [latestKitId, checkoutUrl, isCheckoutExternal, isMobile, trackEvent],
  );
};
